import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable, OperatorFunction, Subject, debounceTime, distinctUntilChanged, filter, map, merge, shareReplay, takeWhile } from 'rxjs';
import { HelperFunctions } from 'src/app/util/util-functions';

export interface FilterOption<T> {
  prop: string;
  value: T;
}
@Component({
  selector: 'app-typeahead',
  templateUrl: './typeahead.component.html',
})
export class TypeaheadComponent<T> implements OnDestroy, OnInit {

  @Input() resetInput: boolean = false;
  @Input() editable: boolean = true;
  @Input() control: FormControl;
  @Input() prop: string;
  @Input() searchOptions: Observable<T[]>;
  @Input() classes: string = "typeahead-relative";
  @Input() filterOptions: FilterOption<any>;
  @Output() selectEvent = new EventEmitter<T>();
  @ViewChild('instance', { static: true }) instance: NgbTypeahead;

  private originalSearchOptions: T[];
  private alive: boolean = true;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  public ngOnInit(): void {
    this.searchOptions.pipe(takeWhile(() => this.alive), shareReplay(1))
      .pipe(takeWhile(() => this.alive))
      .subscribe((results: T[]) => {
        let currentResults = results;
        if (this.filterOptions && this.prop) {
          currentResults = results.filter((options: any) => options[this.filterOptions.prop] === this.filterOptions.value)
        }
        this.originalSearchOptions = currentResults;
      });
  }

  public formatter = (result: any): string => this.prop ? result[this.prop] : result;
  /**
* A function that search the value in the filtered option list
* @returns { OperatorFunction<string, T[]>}
*/
  search: OperatorFunction<string, readonly T[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) => (term === '' ? this.originalSearchOptions : this.originalSearchOptions.filter((v: any) => {
        const value: string = this.prop ? v[this.prop] : v;
        return value.toLowerCase().indexOf(term.toLowerCase()) > -1
      }))));
  };

  onSelect(event: NgbTypeaheadSelectItemEvent) {
    const item = event.item;
    this.selectEvent.emit(item);
    if (this.resetInput)
    event.preventDefault();
  }

  /**
  * A function that checks if the control is invalid
  * @returns {boolean}
  */
  hasErrors(): boolean {
    return HelperFunctions.controlIsInvalid(this.control);
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
